/**
 * Moteur d'interpolation — logique pure (§4 : aucune dépendance).
 *
 * Le temps n'est jamais lu ici : il est **fourni** par l'appelant à chaque `update()`.
 * C'est ce qui rend le moteur intégralement testable sans navigateur, et ce qui garantit
 * qu'aucune animation ne dérive quand l'onglet est masqué et que les frames s'espacent.
 *
 * Mouvement réduit (E§1) : les animations ne disparaissent pas, elles deviennent
 * instantanées. Le joueur doit toujours savoir que son geste a été pris en compte —
 * une absence de retour serait un bug, pas une accessibilité.
 */

import { clamp01, EASING_FUNCTIONS, lerp } from './easing';
import type { EasingName } from './easing';

export interface TweenSpec {
  readonly from: number;
  readonly to: number;
  readonly durationMs: number;
  readonly easing?: EasingName;
  /** Retard avant démarrage, pour les cascades (dépliage de terrain, volée de pièces). */
  readonly delayMs?: number;
  readonly onUpdate: (value: number) => void;
  readonly onComplete?: () => void;
}

export interface TweenHandle {
  /** Interrompt sans appeler `onComplete` : l'animation est abandonnée, pas terminée. */
  cancel(): void;
  /** Saute directement à la fin, `onUpdate` puis `onComplete` compris. */
  finish(): void;
  readonly done: boolean;
}

interface ActiveTween {
  spec: TweenSpec;
  startedAt: number;
  done: boolean;
  cancelled: boolean;
}

export interface TweenRunnerOptions {
  /**
   * Quand vrai, toute interpolation s'applique immédiatement à sa valeur finale.
   * Lu à chaque ajout, pas figé à la construction : le joueur peut changer le réglage
   * système en cours de partie.
   */
  readonly prefersReducedMotion?: () => boolean;
}

/**
 * Ordonnanceur d'interpolations. Une seule instance suffit au jeu entier : on évite
 * ainsi une `requestAnimationFrame` par animation, qui est le premier coût caché des
 * interfaces animées.
 */
export class TweenRunner {
  private readonly active: ActiveTween[] = [];
  private readonly reducedMotion: () => boolean;

  constructor(options: TweenRunnerOptions = {}) {
    this.reducedMotion = options.prefersReducedMotion ?? ((): boolean => false);
  }

  get activeCount(): number {
    return this.active.length;
  }

  /** Ajoute une interpolation. `now` doit venir de la même horloge que `update()`. */
  add(spec: TweenSpec, now: number): TweenHandle {
    if (this.reducedMotion() || spec.durationMs <= 0) {
      spec.onUpdate(spec.to);
      spec.onComplete?.();
      return { cancel: () => undefined, finish: () => undefined, done: true };
    }

    const entry: ActiveTween = {
      spec,
      startedAt: now + (spec.delayMs ?? 0),
      done: false,
      cancelled: false,
    };
    this.active.push(entry);

    return {
      cancel: () => {
        entry.cancelled = true;
      },
      finish: () => {
        if (entry.done || entry.cancelled) return;
        entry.done = true;
        spec.onUpdate(spec.to);
        spec.onComplete?.();
      },
      get done() {
        return entry.done || entry.cancelled;
      },
    };
  }

  /**
   * Fait avancer toutes les interpolations. À appeler une fois par frame.
   *
   * Les entrées terminées sont retirées par compactage sur place plutôt que par
   * `filter`, qui allouerait un tableau à chaque frame — E§3 interdit toute allocation
   * dans la boucle de rendu.
   */
  update(now: number): void {
    let write = 0;

    // Boucle indexée assumée : l'index d'écriture diffère de l'index de lecture.
    // Un `for-of` imposerait un tableau intermédiaire à chaque frame, ce qu'interdit E§3.
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let read = 0; read < this.active.length; read++) {
      const entry = this.active[read];
      if (entry === undefined) continue;

      if (entry.cancelled || entry.done) continue;

      const elapsed = now - entry.startedAt;

      if (elapsed < 0) {
        this.active[write++] = entry;
        continue;
      }

      const progress = clamp01(elapsed / entry.spec.durationMs);
      const ease = EASING_FUNCTIONS[entry.spec.easing ?? 'out'];
      entry.spec.onUpdate(lerp(entry.spec.from, entry.spec.to, ease(progress)));

      if (progress >= 1) {
        entry.done = true;
        entry.spec.onComplete?.();
      } else {
        this.active[write++] = entry;
      }
    }

    this.active.length = write;
  }

  /** Termine tout immédiatement — changement d'écran, prestige, quitter une île. */
  finishAll(): void {
    for (const entry of this.active) {
      if (entry.done || entry.cancelled) continue;
      entry.done = true;
      entry.spec.onUpdate(entry.spec.to);
      entry.spec.onComplete?.();
    }
    this.active.length = 0;
  }

  /** Abandonne tout sans appeler les fins. */
  cancelAll(): void {
    for (const entry of this.active) {
      entry.cancelled = true;
    }
    this.active.length = 0;
  }
}

/**
 * Point sur une courbe de Bézier quadratique, pour le vol des pièces (E§1.2).
 * Une pièce qui va en ligne droite du bâtiment au compteur paraît tirée par un fil ;
 * un arc lui donne du poids.
 */
export function quadraticBezier(
  fromX: number,
  fromY: number,
  controlX: number,
  controlY: number,
  toX: number,
  toY: number,
  t: number,
): { x: number; y: number } {
  const p = clamp01(t);
  const inverse = 1 - p;
  const a = inverse * inverse;
  const b = 2 * inverse * p;
  const c = p * p;

  return {
    x: a * fromX + b * controlX + c * toX,
    y: a * fromY + b * controlY + c * toY,
  };
}

/**
 * Oscillation amortie, pour la secousse de refus (E§1.3) et le tremblement du
 * prestige (E§1.6). Rend une valeur dans [-amplitude, amplitude] qui s'éteint.
 */
export function damptedShake(progress: number, amplitude: number, oscillations: number): number {
  const p = clamp01(progress);
  return Math.sin(p * Math.PI * 2 * oscillations) * amplitude * (1 - p);
}
